import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {Dropdown} from "primereact/dropdown";
import {TabPanel, TabView} from "primereact/tabview";
import {InputText} from "primereact/inputtext";
import {ColorPicker} from "primereact/colorpicker";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import HeaderFooterDialogFields from "../../field/page/HeaderFooterDialogFields";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import {
	HeaderFooterOptions,
	OptionsType,
	OptionsTypeDate,
	OptionsTypePage
} from "../../../../shared/models/HeaderFooterOptions";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Environment,
	Functions,
	HeaderFooter,
	HeadersFootersDocument,
	Information,
	OptionsFont,
	OptionsMargin,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxOptionsOptions} from "../../../../shared/models/PortalToolboxOptionsOptions";
import {
	HeaderFooterFunction,
	PortalHeaderFooterToolboxOptions
} from "../../../../shared/models/PortalHeaderFooterToolboxOptions";
import {PortalAddHeaderFooter} from "../../../../shared/models/PortalAddHeaderFooter";
import {PortalPagesHeaderFooter} from "../../../../shared/models/PortalPagesHeaderFooter";
import {PortalHeaderFooterPositions} from "../../../../shared/models/PortalHeaderFooterPositions";
import PageMode from "../../../../shared/models/PageMode";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import DocumentSettings from "../../../document/DocumentSettings";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import MenuButton from "../../../../shared/menu/MenuButton";
import moment from "moment";
import {SelectItem} from "primereact/selectitem";
import DropdownFilter from "../../../../shared/components/DropdownFilter";

interface HeaderFooterDialogProperties extends PortalDialogProperties<PortalToolboxOptionsOptions> {
	userSettings: Immutable<UserSettings>;
}

interface HeaderFooterDialogStatus extends PortalDialogState<PortalToolboxOptionsOptions> {
	currentPage: string;
	pages: number;
	hasHeaderFooter: boolean;
	panel: number;
	unit: LengthUnit;
}

export class HeaderFooterDialogDefinition extends PortalDialog<PortalToolboxOptionsOptions,
	HeaderFooterDialogProperties, HeaderFooterDialogStatus> {
	private readonly variableStart: string = "<<";
	private readonly variableEnd: string = ">>";

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			currentPage: "1",
			pages: 1,
			hasHeaderFooter: false,
			panel: 0,
			fieldErrors: new Map<HeaderFooterDialogFields, string>(),
			unit: LengthUnit.MILLIMETER
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editHeaderFooter, Functions.getEditHeaderFooterDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.HEADER_FOOTER, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: PortalHeaderFooterToolboxOptions | undefined =
			this.state.parameter?.data.headerFooter as PortalHeaderFooterToolboxOptions;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddHeaderFooter = parameter.add as PortalAddHeaderFooter;

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.hasHeaderFooter || !this.state.fields?.contains(HeaderFooterDialogFields.MODE)
				}>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-mode">
							<Translate {...this.props} data="header_footer.mode.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-mode"
									  value={FunctionHelper.applyValue(
										  parameter.mode, PortalHeaderFooterToolboxOptions.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.mode.remove", {ns: "dialog"}
											  ),
											  value: HeaderFooterFunction.REMOVE
										  },
										  {
											  label: Translate.value(
												  "header_footer.mode.add", {ns: "dialog"}
											  ),
											  value: HeaderFooterFunction.ADD
										  }
									  ]}
									  onChange={(e) => this.onChange(HeaderFooterDialogFields.MODE, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<div className="p-col-12 p-grid" hidden={parameter.mode === HeaderFooterFunction.REMOVE}>
					<TabView activeIndex={this.state.panel} onTabChange={(e) => this.setState({panel: e.index})}>
						<TabPanel header={Translate.value("header_footer.general.label", {ns: "dialog"})}
								  headerClassName="control-tab-general" contentClassName="general-tab-panel">
							{this.renderGeneralTab(addParameter)}
						</TabPanel>
						<TabPanel header={Translate.value("header_footer.header.label", {ns: "dialog"})}
								  headerClassName="control-tab-header" contentClassName="header-tab-panel">
							{this.renderHeaderTab(addParameter)}
						</TabPanel>
						<TabPanel header={Translate.value("header_footer.footer.label", {ns: "dialog"})}
								  headerClassName="control-tab-footer" contentClassName="footer-tab-panel">
							{this.renderFooterTab(addParameter)}
						</TabPanel>
					</TabView>
				</div>
			</>
		);
	}

	private renderGeneralTab(parameter: PortalAddHeaderFooter): ReactNode {
		let pages: PortalPagesHeaderFooter =
			parameter.headerFooterContent?.pages as PortalPagesHeaderFooter;
		let errors: Map<HeaderFooterDialogFields, string> = this.state.fieldErrors;

		let fonts: Array<SelectItem> = [];
		for (let font of FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		)) {
			fonts.push({
				label: font,
				value: font
			});
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(HeaderFooterDialogFields.FONT_FAMILY) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.FONT_COLOR) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.FONT_SIZE) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.UNDERLINE)
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FONT_FAMILY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-family">
							<Translate {...this.props} data="header_footer.general.font.family" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<DropdownFilter inputId="control-font-family"
											value={FunctionHelper.applyValue(
												parameter.headerFooterContent?.font?.name, OptionsFont.getNameDefault()
											)}
											onChange={(e) => this.onChange(
												HeaderFooterDialogFields.FONT_FAMILY, e.value
											)}
											appendTo={document.getElementById("root")} options={fonts}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FONT_SIZE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-size">
							<Translate {...this.props} data="header_footer.general.font.size" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-font-size" showButtons={true}
											value={FunctionHelper.applyValue(
												parameter.headerFooterContent?.font?.size, OptionsFont.getSizeDefault()
											)}
											onChange={(e) => this.onChange(HeaderFooterDialogFields.FONT_SIZE, e.value)}
											min={OptionsFont.getSizeMin()} max={OptionsFont.getSizeMax()}
											appendTo={document.getElementById("root")}
											options={[
												{label: "8", value: 8},
												{label: "9", value: 9},
												{label: "10", value: 10},
												{label: "11", value: 11},
												{label: "12", value: 12},
												{label: "14", value: 14},
												{label: "16", value: 16},
												{label: "18", value: 18},
												{label: "20", value: 20},
												{label: "24", value: 24},
												{label: "28", value: 28},
												{label: "36", value: 36},
												{label: "48", value: 48},
												{label: "72", value: 72}
											]}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FONT_COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-color">
							<Translate {...this.props} data="header_footer.general.font.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className={"p-inputgroup" + (
								errors.has(HeaderFooterDialogFields.FONT_COLOR) ? " p-invalid" : ""
							)}>
								<InputText
									className={errors.has(HeaderFooterDialogFields.FONT_COLOR) ? "p-invalid" : ""}
									tooltip={errors.has(HeaderFooterDialogFields.FONT_COLOR) ? Translate.value(
										errors.get(HeaderFooterDialogFields.FONT_COLOR)!, {ns: "message"}
									) : ""}
									value={FunctionHelper.applyValue(
										parameter.headerFooterContent?.font?.color, OptionsFont.getColorDefault()
									)}
									onInput={(e) => this.onChange(
										HeaderFooterDialogFields.FONT_COLOR, e.currentTarget.value
									)}/>
								<ColorPicker inputId="control-font-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 OptionsFont.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 parameter.headerFooterContent?.font?.color,
												 OptionsFont.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 HeaderFooterDialogFields.FONT_COLOR, "#" + e.value
											 )} appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(HeaderFooterDialogFields.FONT_COLOR) ?
								Translate.value(
									errors.get(HeaderFooterDialogFields.FONT_COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.UNDERLINE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-font-underline"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-font-underline"
										 checked={FunctionHelper.applyValue(
											 parameter.headerFooterContent?.font?.underline,
											 OptionsFont.getUnderlineDefault()
										 )}
										 onChange={(e) => this.onChange(HeaderFooterDialogFields.UNDERLINE, e.value)}/>
							<label className="switch-label" htmlFor="control-font-underline">
								<Translate {...this.props} data="header_footer.general.font.underline"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_TOP) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_RIGHT) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_BOTTOM) &&
					!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_LEFT)
				}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_LEFT)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-left"
							   hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_LEFT)}>
							<Translate {...this.props} data="header_footer.general.display.margin.left"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-left" suffix={" " + this.state.unit} step={1}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.headerFooterContent?.margin?.left,
													 OptionsMargin.getLeftDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 HeaderFooterDialogFields.MARGIN_LEFT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												HeaderFooterDialogFields.MARGIN_LEFT, OptionsMargin.getLeftDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_RIGHT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-right"
							   hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_RIGHT)}>
							<Translate {...this.props} data="header_footer.general.display.margin.right"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-right" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.headerFooterContent?.margin?.right,
													 OptionsMargin.getRightDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 HeaderFooterDialogFields.MARGIN_RIGHT, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												HeaderFooterDialogFields.MARGIN_RIGHT, OptionsMargin.getRightDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_TOP)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-top"
							   hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_TOP)}>
							<Translate {...this.props} data="header_footer.general.display.margin.top"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-top" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.headerFooterContent?.margin?.top,
													 OptionsMargin.getTopDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 HeaderFooterDialogFields.MARGIN_TOP, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												HeaderFooterDialogFields.MARGIN_TOP, OptionsMargin.getTopDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_BOTTOM)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-margin-bottom"
							   hidden={!this.state.fields?.contains(HeaderFooterDialogFields.MARGIN_BOTTOM)}>
							<Translate {...this.props} data="header_footer.general.display.margin.bottom"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-margin-bottom" step={1} suffix={" " + this.state.unit}
											 maxFractionDigits={2} minFractionDigits={2} showButtons={true}
											 useGrouping={false}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 parameter.headerFooterContent?.margin?.bottom,
													 OptionsMargin.getBottomDefault()
												 ), LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 onChange={(e) => this.onChange(
												 HeaderFooterDialogFields.MARGIN_BOTTOM, e.value
											 )}/>
								<div className="p-inputgroup-addon vertical">
									<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
											onClick={() => this.onChange(
												HeaderFooterDialogFields.MARGIN_BOTTOM, OptionsMargin.getBottomDefault()
											)}/>
								</div>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.PAGES_FROM)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages-from">
							<Translate {...this.props} data="header_footer.general.pages.from" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-pages-from" showButtons={true} step={1} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 pages.from, PortalPagesHeaderFooter.getFromDefault()
										 )}
										 min={PortalPagesHeaderFooter.getFromMin()}
										 max={FunctionHelper.applyValue(
											 pages.to, PortalPagesHeaderFooter.getToDefault()
										 )}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.PAGES_FROM, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.PAGES_TO)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages-to">
							<Translate {...this.props} data="header_footer.general.pages.to" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputNumber inputId="control-pages-to" showButtons={true} step={1} useGrouping={false}
											 value={FunctionHelper.applyValue(
												 pages.to, PortalPagesHeaderFooter.getToDefault()
											 )}
											 min={FunctionHelper.applyValue(
												 pages.from, PortalPagesHeaderFooter.getToMin()
											 )}
											 max={this.state.pages}
											 onChange={(e) =>
												 this.onChange(HeaderFooterDialogFields.PAGES_TO, e.value)
											 }/>
								<span className="p-inputgroup-addon">
									{Translate.value("inputs.pagerange.of", {pages: this.state.pages})}
								</span>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.PAGES_FIRST)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages-first">
							<Translate {...this.props} data="header_footer.general.pages.first" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber step={1} inputId="control-pages-first" max={this.state.pages}
										 showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter.headerFooterContent?.firstPageNumber,
											 HeaderFooter.getFirstPageNumberDefault()
										 )}
										 min={HeaderFooter.getFirstPageNumberMin()}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.PAGES_FIRST, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.PAGES_FILTER)}>
						<label className="p-col-12 p-sm-5"/>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-filter"
									  value={FunctionHelper.applyValue(
										  pages.mode, PortalPagesHeaderFooter.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.general.pages.filter.all", {ns: "dialog"}
											  ),
											  value: PageMode.All
										  },
										  {
											  label: Translate.value(
												  "header_footer.general.pages.filter.even", {ns: "dialog"}
											  ),
											  value: PageMode.Even
										  },
										  {
											  label: Translate.value(
												  "header_footer.general.pages.filter.odd", {ns: "dialog"}
											  ),
											  value: PageMode.Odd
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.PAGES_FILTER, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.header_footer.pages"} namespace={"help"}
								   markdown={true}/>
					</Helper>
				</fieldset>
			</>
		);
	}

	private renderPreview(value: string): ReactNode {
		let variableRegex: RegExp = new RegExp(/(<<.*?>>)+?/g);

		while (variableRegex.test(value)) {
			value = value.replace(variableRegex, (variable: string) => {
				variable = variable.replace(this.variableStart, "");
				variable = variable.replace(this.variableEnd, "");

				let DAY_PART: string = "d{1,2}(?!.*[d])";
				let MONTH_PART: string = "m{1,2}(?!.*[m])";
				let YEAR_PART: string = "y{2,4}(?!.*[y])";
				let DATE_PART: string = "(" + DAY_PART + "|" + MONTH_PART + "|" + YEAR_PART + ")";
				let SEPARATORS: string = "(.)?";
				let dateRegex: RegExp = new RegExp(
					"^" + DATE_PART + SEPARATORS + DATE_PART + "(?:" + SEPARATORS + DATE_PART + ")?$", "g"
				);
				if (dateRegex.test(variable)) {
					return moment().format(variable.toUpperCase());
				}

				let currentPageRegex: RegExp = new RegExp(/(^| )+(i)($| )+/g);
				while (currentPageRegex.test(variable)) {
					variable = variable.replace(
						currentPageRegex,
						(match, group1, group2, group3) => {
							return group1 + this.state.currentPage + group3;
						}
					);
				}

				let PagesRegex: RegExp = new RegExp(/(^| )+(n)($| )+/g);
				while (PagesRegex.test(variable)) {
					variable = variable.replace(
						PagesRegex,
						(match, group1, group2, group3) => {
							return group1 + this.state.pages.toString() + group3;
						}
					);
				}

				return variable;
			});
		}

		return value;
	}

	private renderHeaderTab(parameter: PortalAddHeaderFooter): ReactNode {
		let header: PortalHeaderFooterPositions = parameter.headerFooterContent?.header as PortalHeaderFooterPositions;

		return (
			<>
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.header_footer.modes"} namespace={"help"} markdown={true}/>
				</Helper>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-type">
							<Translate {...this.props} data="header_footer.header.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-left-type"
									  value={FunctionHelper.applyValue(
										  header.leftOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_LEFT_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<div className="p-inputgroup">
								<InputText id="control-header-left-custom"
										   value={FunctionHelper.applyValue(
											   header.leftOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.HEADER_LEFT_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_LEFT_CUSTOM,
																FunctionHelper.applyValue(
																	header.leftOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_LEFT_CUSTOM,
																FunctionHelper.applyValue(
																	header.leftOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-left-date"
									  value={FunctionHelper.applyValue(
										  header.leftOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_LEFT_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-left-pages"
									  value={FunctionHelper.applyValue(
										  header.leftOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_LEFT_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-left-bates-prefix"
									   value={FunctionHelper.applyValue(
										   header.leftOptions?.batesPrefix, HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_LEFT_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-left-bates-suffix"
									   value={FunctionHelper.applyValue(
										   header.leftOptions?.batesSuffix, HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_LEFT_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_LEFT_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-left-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-header-left-bates-digits" step={1} showButtons={true} min={1}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 header.leftOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.HEADER_LEFT_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						header.leftOptions?.type === OptionsType.NONE || header.leftOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									header.leftOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-type">
							<Translate {...this.props} data="header_footer.header.center" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-center-type"
									  value={FunctionHelper.applyValue(
										  header.centerOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_CENTER_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText id="control-header-center-custom"
										   value={FunctionHelper.applyValue(
											   header.centerOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.HEADER_CENTER_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_CENTER_CUSTOM,
																FunctionHelper.applyValue(
																	header.centerOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_CENTER_CUSTOM,
																FunctionHelper.applyValue(
																	header.centerOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-center-date"
									  value={FunctionHelper.applyValue(
										  header.centerOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_CENTER_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-center-pages"
									  value={FunctionHelper.applyValue(
										  header.centerOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_CENTER_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-center-bates-prefix"
									   value={FunctionHelper.applyValue(
										   header.centerOptions?.batesPrefix,
										   HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_CENTER_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-center-bates-suffix"
									   value={FunctionHelper.applyValue(
										   header.centerOptions?.batesSuffix,
										   HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_CENTER_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_CENTER_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-center-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-header-center-bates-digits" step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 header.centerOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 min={HeaderFooterOptions.getBatesDigitsMin()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.HEADER_CENTER_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						header.centerOptions?.type === OptionsType.NONE ||
						header.centerOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									header.centerOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-type">
							<Translate {...this.props} data="header_footer.header.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-right-type"
									  value={FunctionHelper.applyValue(
										  header.rightOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_RIGHT_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText id="control-header-right-custom"
										   value={FunctionHelper.applyValue(
											   header.rightOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.HEADER_RIGHT_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_RIGHT_CUSTOM,
																FunctionHelper.applyValue(
																	header.rightOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.HEADER_RIGHT_CUSTOM,
																FunctionHelper.applyValue(
																	header.rightOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-right-date"
									  value={FunctionHelper.applyValue(
										  header.rightOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_RIGHT_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-header-right-pages"
									  value={FunctionHelper.applyValue(
										  header.rightOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.HEADER_RIGHT_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-right-bates-prefix"
									   value={FunctionHelper.applyValue(
										   header.rightOptions?.batesPrefix,
										   HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_RIGHT_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-header-right-bates-suffix"
									   value={FunctionHelper.applyValue(
										   header.rightOptions?.batesSuffix, HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.HEADER_RIGHT_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 header.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.HEADER_RIGHT_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-header-right-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-header-right-bates-digits" step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 header.rightOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 min={HeaderFooterOptions.getBatesDigitsMin()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.HEADER_RIGHT_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						header.rightOptions?.type === OptionsType.NONE ||
						header.rightOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									header.rightOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderFooterTab(parameter: PortalAddHeaderFooter): ReactNode {
		let footer: PortalHeaderFooterPositions =
			parameter.headerFooterContent?.footer as PortalHeaderFooterPositions;

		return (
			<>
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.header_footer.modes"} namespace={"help"} markdown={true}/>
				</Helper>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-type">
							<Translate {...this.props} data="header_footer.footer.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-left-type"
									  value={FunctionHelper.applyValue(
										  footer.leftOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_LEFT_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText id="control-footer-left-custom"
										   value={FunctionHelper.applyValue(
											   footer.leftOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.FOOTER_LEFT_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_LEFT_CUSTOM,
																FunctionHelper.applyValue(
																	footer.leftOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_LEFT_CUSTOM,
																FunctionHelper.applyValue(
																	footer.leftOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-left-date"
									  value={FunctionHelper.applyValue(
										  footer.leftOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_LEFT_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-left-pages"
									  value={FunctionHelper.applyValue(
										  footer.leftOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_LEFT_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-left-bates-prefix"
									   value={FunctionHelper.applyValue(
										   footer.leftOptions?.batesPrefix, HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_LEFT_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-left-bates-suffix"
									   value={FunctionHelper.applyValue(
										   footer.leftOptions?.batesSuffix, HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_LEFT_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.leftOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_LEFT_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-left-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-footer-left-bates-digits" step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 footer.leftOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 min={HeaderFooterOptions.getBatesDigitsMin()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.FOOTER_LEFT_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						footer.leftOptions?.type === OptionsType.NONE || footer.leftOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									footer.leftOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-type">
							<Translate {...this.props} data="header_footer.footer.center" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-center-type"
									  value={FunctionHelper.applyValue(
										  footer.centerOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_CENTER_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText id="control-footer-center-custom"
										   value={FunctionHelper.applyValue(
											   footer.centerOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.FOOTER_CENTER_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_CENTER_CUSTOM,
																FunctionHelper.applyValue(
																	footer.centerOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_CENTER_CUSTOM,
																FunctionHelper.applyValue(
																	footer.centerOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-center-date"
									  value={FunctionHelper.applyValue(
										  footer.centerOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_CENTER_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-center-pages"
									  value={FunctionHelper.applyValue(
										  footer.centerOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_CENTER_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-center-bates-prefix"
									   value={FunctionHelper.applyValue(
										   footer.centerOptions?.batesPrefix,
										   HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_CENTER_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-center-bates-suffix"
									   value={FunctionHelper.applyValue(
										   footer.centerOptions?.batesSuffix,
										   HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_CENTER_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.centerOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_CENTER_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-center-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-footer-center-bates-digits" step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 footer.centerOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 min={HeaderFooterOptions.getBatesDigitsMin()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.FOOTER_CENTER_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						footer.centerOptions?.type === OptionsType.NONE ||
						footer.centerOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									footer.centerOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_TYPE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-type">
							<Translate {...this.props} data="header_footer.footer.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-right-type"
									  value={FunctionHelper.applyValue(
										  footer.rightOptions?.type, HeaderFooterOptions.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "header_footer.options.type.none", {ns: "dialog"}
											  ),
											  value: OptionsType.NONE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.custom", {ns: "dialog"}
											  ),
											  value: OptionsType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.date", {ns: "dialog"}
											  ),
											  value: OptionsType.DATE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.page", {ns: "dialog"}
											  ),
											  value: OptionsType.PAGE
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.type.bates", {ns: "dialog"}
											  ),
											  value: OptionsType.BATES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_RIGHT_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.CUSTOM ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_CUSTOM)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-custom">
							<Translate {...this.props} data="header_footer.options.custom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText id="control-footer-right-custom"
										   value={FunctionHelper.applyValue(
											   footer.rightOptions?.custom, HeaderFooterOptions.getCustomDefault()
										   )}
										   onInput={(e) => this.onChange(
											   HeaderFooterDialogFields.FOOTER_RIGHT_CUSTOM, e.currentTarget.value
										   )}/>
								<MenuButton {...this.props} icon="icon icon-angle-down"
											menuItems={
												[
													{
														label: Translate.value(
															"header_footer.options.custom.date.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_RIGHT_CUSTOM,
																FunctionHelper.applyValue(
																	footer.rightOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.date.value",
																	{ns: "dialog"}
																)
															)
														}
													},
													{
														label: Translate.value(
															"header_footer.options.custom.page.label",
															{ns: "dialog"}
														),
														command: () => {
															this.onChange(
																HeaderFooterDialogFields.FOOTER_RIGHT_CUSTOM,
																FunctionHelper.applyValue(
																	footer.rightOptions?.custom,
																	HeaderFooterOptions.getCustomDefault()
																) +
																Translate.value(
																	"header_footer.options.custom.page.value",
																	{ns: "dialog"}
																)
															)
														}
													}
												]
											}/>
							</div>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.DATE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_DATE)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-date">
							<Translate {...this.props} data="header_footer.options.date" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-right-date"
									  value={FunctionHelper.applyValue(
										  footer.rightOptions?.date, HeaderFooterOptions.getDateDefault()
									  )}
									  options={[
										  {label: OptionsTypeDate["M/D"], value: OptionsTypeDate["M/D"]},
										  {label: OptionsTypeDate["M/D/YY"], value: OptionsTypeDate["M/D/YY"]},
										  {label: OptionsTypeDate["M/D/YYYY"], value: OptionsTypeDate["M/D/YYYY"]},
										  {label: OptionsTypeDate["MM/DD/YY"], value: OptionsTypeDate["MM/DD/YY"]},
										  {label: OptionsTypeDate["MM/DD/YYYY"], value: OptionsTypeDate["MM/DD/YYYY"]},
										  {label: OptionsTypeDate["D/M/YY"], value: OptionsTypeDate["D/M/YY"]},
										  {label: OptionsTypeDate["D/M/YYYY"], value: OptionsTypeDate["D/M/YYYY"]},
										  {label: OptionsTypeDate["DD/MM/YY"], value: OptionsTypeDate["DD/MM/YY"]},
										  {label: OptionsTypeDate["DD/MM/YYYY"], value: OptionsTypeDate["DD/MM/YYYY"]},
										  {label: OptionsTypeDate["MM/YY"], value: OptionsTypeDate["MM/YY"]},
										  {label: OptionsTypeDate["MM/YYYY"], value: OptionsTypeDate["MM/YYYY"]},
										  {label: OptionsTypeDate["M.D"], value: OptionsTypeDate["M.D"]},
										  {label: OptionsTypeDate["M.D.YY"], value: OptionsTypeDate["M.D.YY"]},
										  {label: OptionsTypeDate["M.D.YYYY"], value: OptionsTypeDate["M.D.YYYY"]},
										  {label: OptionsTypeDate["MM.DD.YY"], value: OptionsTypeDate["MM.DD.YY"]},
										  {label: OptionsTypeDate["MM.DD.YYYY"], value: OptionsTypeDate["MM.DD.YYYY"]},
										  {label: OptionsTypeDate["D.M.YY"], value: OptionsTypeDate["D.M.YY"]},
										  {label: OptionsTypeDate["D.M.YYYY"], value: OptionsTypeDate["D.M.YYYY"]},
										  {label: OptionsTypeDate["DD.MM.YY"], value: OptionsTypeDate["DD.MM.YY"]},
										  {label: OptionsTypeDate["DD.MM.YYYY"], value: OptionsTypeDate["DD.MM.YYYY"]},
										  {label: OptionsTypeDate["MM.YY"], value: OptionsTypeDate["MM.YY"]},
										  {label: OptionsTypeDate["MM.YYYY"], value: OptionsTypeDate["MM.YYYY"]}
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_RIGHT_DATE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.PAGE ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_PAGES)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-pages">
							<Translate {...this.props} data="header_footer.options.pages.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-footer-right-pages"
									  value={FunctionHelper.applyValue(
										  footer.rightOptions?.page, HeaderFooterOptions.getPageDefault()
									  )}
									  options={[
										  {label: OptionsTypePage.I, value: OptionsTypePage.I},
										  {label: OptionsTypePage["I_/_N"], value: OptionsTypePage["I_/_N"]},
										  {
											  label: Translate.value(
												  "header_footer.options.pages.i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.I_OF_N
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I
										  },
										  {
											  label: Translate.value(
												  "header_footer.options.pages.page_i_of_n", {ns: "dialog"}
											  ),
											  value: OptionsTypePage.PAGE_I_OF_N
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  HeaderFooterDialogFields.FOOTER_RIGHT_PAGES, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_BATES_PREFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-bates-prefix">
							<Translate {...this.props} data="header_footer.options.bates.prefix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-right-bates-prefix"
									   value={FunctionHelper.applyValue(
										   footer.rightOptions?.batesPrefix,
										   HeaderFooterOptions.getBatesPrefixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_RIGHT_BATES_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_BATES_SUFFIX)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-bates-suffix">
							<Translate {...this.props} data="header_footer.options.bates.suffix" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-footer-right-bates-suffix"
									   value={FunctionHelper.applyValue(
										   footer.rightOptions?.batesSuffix,
										   HeaderFooterOptions.getBatesSuffixDefault()
									   )}
									   onInput={(e) => this.onChange(
										   HeaderFooterDialogFields.FOOTER_RIGHT_BATES_SUFFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 footer.rightOptions?.type !== OptionsType.BATES ||
							 !this.state.fields?.contains(HeaderFooterDialogFields.FOOTER_RIGHT_BATES_DIGITS)
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-footer-right-bates-digits">
							<Translate {...this.props} data="header_footer.options.bates.digits" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-footer-right-bates-digits" step={1} showButtons={true}
										 value={FunctionHelper.applyValue(
											 footer.rightOptions?.batesDigits,
											 HeaderFooterOptions.getBatesDigitsDefault()
										 )}
										 min={HeaderFooterOptions.getBatesDigitsMin()} useGrouping={false}
										 onChange={(e) => this.onChange(
											 HeaderFooterDialogFields.FOOTER_RIGHT_BATES_DIGITS, e.value
										 )}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						footer.rightOptions?.type === OptionsType.NONE ||
						footer.rightOptions?.type === OptionsType.BATES
					}>
						<label className="p-col-12 p-sm-5">
							<Translate {...this.props} data="header_footer.options.preview" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							{this.renderPreview(
								this.computeContents(
									footer.rightOptions!, parameter.headerFooterContent!.firstPageNumber!
								)
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["header-footer-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.header_footer.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: HeaderFooterDialogFields, value: any): void {
		let parameter: PortalHeaderFooterToolboxOptions =
			this.state.parameter!.data.headerFooter as PortalHeaderFooterToolboxOptions;
		let addParameter: PortalAddHeaderFooter = parameter.add as PortalAddHeaderFooter;
		let pages: PortalPagesHeaderFooter =
			addParameter.headerFooterContent?.pages as PortalPagesHeaderFooter;
		let header: PortalHeaderFooterPositions =
			addParameter.headerFooterContent?.header as PortalHeaderFooterPositions;
		let footer: PortalHeaderFooterPositions =
			addParameter.headerFooterContent?.footer as PortalHeaderFooterPositions;
		let errors: Map<HeaderFooterDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case HeaderFooterDialogFields.MODE:
				parameter.mode = value;
				break;
			case HeaderFooterDialogFields.FONT_FAMILY:
				addParameter.headerFooterContent!.font!.name = value;
				break;
			case HeaderFooterDialogFields.FONT_SIZE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OptionsFont.getSizeDefault();
				}

				addParameter.headerFooterContent!.font!.size = value;
				break;
			case HeaderFooterDialogFields.FONT_COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.headerFooterContent!.font!.color = value;
				break;
			case HeaderFooterDialogFields.UNDERLINE:
				addParameter.headerFooterContent!.font!.underline = value;
				break;
			case HeaderFooterDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OptionsMargin.getLeftDefault();
				}

				addParameter.headerFooterContent!.margin!.left = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case HeaderFooterDialogFields.MARGIN_RIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OptionsMargin.getRightDefault();
				}

				addParameter.headerFooterContent!.margin!.right = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case HeaderFooterDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OptionsMargin.getTopDefault();
				}

				addParameter.headerFooterContent!.margin!.top = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case HeaderFooterDialogFields.MARGIN_BOTTOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = OptionsMargin.getBottomDefault();
				}

				addParameter.headerFooterContent!.margin!.bottom = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case HeaderFooterDialogFields.PAGES_FROM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalPagesHeaderFooter.getFromDefault();
				}

				pages.from = value;
				break;
			case HeaderFooterDialogFields.PAGES_TO:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = PortalPagesHeaderFooter.getToDefault();
				}

				pages.to = value;
				break;
			case HeaderFooterDialogFields.PAGES_FIRST:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooter.getFirstPageNumberDefault();
				}

				addParameter.headerFooterContent!.firstPageNumber = value;
				break;
			case HeaderFooterDialogFields.PAGES_FILTER:
				pages.mode = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_TYPE:
				header.leftOptions!.type = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_CUSTOM:
				header.leftOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_DATE:
				header.leftOptions!.date = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_PAGES:
				header.leftOptions!.page = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_BATES_PREFIX:
				header.leftOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_BATES_SUFFIX:
				header.leftOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.HEADER_LEFT_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				header.leftOptions!.batesDigits = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_TYPE:
				header.centerOptions!.type = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_CUSTOM:
				header.centerOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_DATE:
				header.centerOptions!.date = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_PAGES:
				header.centerOptions!.page = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_BATES_PREFIX:
				header.centerOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_BATES_SUFFIX:
				header.centerOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.HEADER_CENTER_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				header.centerOptions!.batesDigits = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_TYPE:
				header.rightOptions!.type = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_CUSTOM:
				header.rightOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_DATE:
				header.rightOptions!.date = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_PAGES:
				header.rightOptions!.page = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_BATES_PREFIX:
				header.rightOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_BATES_SUFFIX:
				header.rightOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.HEADER_RIGHT_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				header.rightOptions!.batesDigits = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_TYPE:
				footer.leftOptions!.type = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_CUSTOM:
				footer.leftOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_DATE:
				footer.leftOptions!.date = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_PAGES:
				footer.leftOptions!.page = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_BATES_PREFIX:
				footer.leftOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_BATES_SUFFIX:
				footer.leftOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_LEFT_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				footer.leftOptions!.batesDigits = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_TYPE:
				footer.centerOptions!.type = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_CUSTOM:
				footer.centerOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_DATE:
				footer.centerOptions!.date = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_PAGES:
				footer.centerOptions!.page = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_BATES_PREFIX:
				footer.centerOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_BATES_SUFFIX:
				footer.centerOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_CENTER_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				footer.centerOptions!.batesDigits = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_TYPE:
				footer.rightOptions!.type = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_CUSTOM:
				footer.rightOptions!.custom = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_DATE:
				footer.rightOptions!.date = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_PAGES:
				footer.rightOptions!.page = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_BATES_PREFIX:
				footer.rightOptions!.batesPrefix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_BATES_SUFFIX:
				footer.rightOptions!.batesSuffix = value;
				break;
			case HeaderFooterDialogFields.FOOTER_RIGHT_BATES_DIGITS:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = HeaderFooterOptions.getBatesDigitsDefault();
				}

				footer.rightOptions!.batesDigits = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-header-footer"}/>
					<Translate {...this.props} data="header_footer.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalHeaderFooterToolboxOptions | undefined =
			this.state.parameter?.data?.headerFooter as PortalHeaderFooterToolboxOptions;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter?.add) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let addParameter: PortalAddHeaderFooter = parameter.add as PortalAddHeaderFooter;
		let pages: PortalPagesHeaderFooter =
			addParameter.headerFooterContent?.pages as PortalPagesHeaderFooter;

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());
		let pageCount: number = await portalDocument.getNumberOfPages();
		let currentPage: number = documentSettings.activeViewer.currentPage;
		let hasHeaderFooter: boolean = FunctionHelper.applyValue(
			portalDocument.metadata?.headersFooters?.headerFooter, HeadersFootersDocument.getHeaderFooterDefault()
		).length > 0;

		if (!hasHeaderFooter) {
			parameter.mode = HeaderFooterFunction.ADD;
		}

		pages.from = currentPage;
		pages.to = pageCount;
		addParameter.headerFooterContent!.firstPageNumber = currentPage;

		let selectedFont: string | undefined = addParameter.headerFooterContent?.font?.name;
		let fonts: Array<string> = FunctionHelper.applyValue(
			this.props.context.info.environment?.fonts, Environment.getFontsDefault()
		);
		if (!FunctionHelper.exists(selectedFont) || (fonts.length > 0 && fonts.indexOf(selectedFont!) === -1)) {
			addParameter.headerFooterContent!.font!.name = fonts[0];
		}

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			hasHeaderFooter: hasHeaderFooter,
			unit: userSettings.unit,
			currentPage: currentPage.toString()
		});
	};

	private computeContents(parameter: HeaderFooterOptions, firstPageNumber: number): string {
		if (parameter.type !== OptionsType.PAGE) {
			return parameter.computeContents(firstPageNumber);
		}

		switch (parameter.page) {
			case OptionsTypePage.I_OF_N:
				return this.variableStart +
					Translate.value("header_footer.options.pages.i_of_n", {ns: "dialog"}) +
					this.variableEnd;
			case OptionsTypePage.PAGE_I:
				return this.variableStart +
					Translate.value("header_footer.options.pages.page_i", {ns: "dialog"}) +
					this.variableEnd;
			case OptionsTypePage.PAGE_I_OF_N:
				return this.variableStart +
					Translate.value("header_footer.options.pages.page_i_of_n", {ns: "dialog"}) +
					this.variableEnd;
			default:
				return this.variableStart + parameter.page + this.variableEnd;
		}
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		let parameter: PortalAddHeaderFooter | undefined =
			this.state.parameter?.data?.headerFooter?.add as PortalAddHeaderFooter;

		let header: PortalHeaderFooterPositions =
			parameter.headerFooterContent?.header as PortalHeaderFooterPositions;
		header.left = this.computeContents(header.leftOptions!, parameter.headerFooterContent!.firstPageNumber!);
		header.center = this.computeContents(header.centerOptions!, parameter.headerFooterContent!.firstPageNumber!);
		header.right = this.computeContents(header.rightOptions!, parameter.headerFooterContent!.firstPageNumber!);

		let footer: PortalHeaderFooterPositions =
			parameter.headerFooterContent?.footer as PortalHeaderFooterPositions;
		footer.left = this.computeContents(footer.leftOptions!, parameter.headerFooterContent!.firstPageNumber!);
		footer.center = this.computeContents(footer.centerOptions!, parameter.headerFooterContent!.firstPageNumber!);
		footer.right = this.computeContents(footer.rightOptions!, parameter.headerFooterContent!.firstPageNumber!);

		let pages: PortalPagesHeaderFooter | undefined =
			parameter.headerFooterContent?.pages as PortalPagesHeaderFooter;
		pages.pageInterval!.pages = pages.from + "-" + pages.to;
		pages.pageInterval!.odd = pages.mode === PageMode.Odd || pages.mode === PageMode.All
		pages.pageInterval!.even = pages.mode === PageMode.Even || pages.mode === PageMode.All

		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let headerFooterOptionsParameter: PortalToolboxOptionsOptions = this.state.parameter!.data.clone();
			let headerFooterParameter: PortalHeaderFooterToolboxOptions =
				headerFooterOptionsParameter.headerFooter as PortalHeaderFooterToolboxOptions;
			if (headerFooterParameter.mode === HeaderFooterFunction.REMOVE) {
				headerFooterParameter.add = undefined;
			} else {
				headerFooterParameter.remove = undefined;
			}

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				options: headerFooterOptionsParameter.toJson()
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.toolbox.headerFooter", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
				this.hide();
			}
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.HEADER_FOOTER;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxOptionsOptions> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}
}

const HeaderFooterDialog = React.memo(HeaderFooterDialogDefinition,
	(
		prevProps: Readonly<HeaderFooterDialogProperties>,
		nextProps: Readonly<HeaderFooterDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default HeaderFooterDialog;